<template>
  <div class="component-wrap">
    <div class="dashboard-actions">
      <md-button
        class="md-primary md-raised"
        :disabled="uploadingScannedPdf"
        @click="openUploadDialog"
      >
        {{ uploadingScannedPdf ? "Uploading..." : "Upload" }}
      </md-button>

      <input
        ref="scannedPdfInput"
        type="file"
        accept=".pdf,application/pdf"
        @change="onScannedPdfSelected"
        style="display: none"
      />
    </div>

    <button v-if="showScrollToTop" @click="scrollToTop" class="scroll-to-top-btn">
      ↑
    </button>

    <div class="main-hdr">
      <Search
        @setResultsOrders="setResultsOrders"
        @searchInput="inputSearch"
        @loadingSearchResults="loadingSearchResults"
      />

      <div
        v-if="!filteredOrders.length"
        class="init-search-desktop md-layout md-alignment-center-center md-raised"
      >
        <div class="no-result-message md-layout-item md-size-70">
          <p>
            Nothing here yet. <br />
            Use search to load artwork and signage.
          </p>
        </div>
      </div>
    </div>

    <installation-dashboard-table
      v-if="filteredOrders.length && !loadingSpinner"
      :installations-prop="filteredOrders"
    ></installation-dashboard-table>
    <AppSpinner
      v-if="loadingSpinner"
      :loading="loadingSpinner"
      class="floating-spinner"
    />
  </div>
</template>

<script>import InstallationDashboardTable from "@/components/InstallerDashboard/InstallationDashboardTable.vue";
// import {sponsorshipStatuses, signageStatuses} from "../../constants"
import { DeepFilterMixin } from "@/mixins/DeepFilterMixin";
import Search from "@/components/Common/Search";
import { InitializeOrderMixin } from "@/mixins/InitializeOrderMixin";
import Cookie from "js-cookie";

export default {
  name: "InstallerDashboardMainFeature",
  mixins: [DeepFilterMixin, InitializeOrderMixin],

  data: function() {
    return {
      orders: [],
      filteredOrders: [],
      loadingSpinner: false,
      snackbar: {
        show: false,
        message: "",
        duration: 4000,
      },
      showScrollToTop: false,
      uploadingScannedPdf: false,
    };
  },

  mounted() {
    window.addEventListener("scroll", this.handleScroll);
    this.handleScroll();
  },

  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },

  created() {
    this.filters = this.$store.getters["getVendorFilters"];
    this.loadInitialOrders();
  },

  beforeRouteLeave(to, from, next) {
    this.$store.dispatch("saveVendorFilters", this.filters);
    this.$store.dispatch("saveVendorOrders", this.filteredOrders);
    next();
  },

  components: {
    Search,
    InstallationDashboardTable,
  },

  methods: {
    showSnackbar(message) {
      this.snackbar.message = message;
      this.snackbar.show = true;
    },

    openUploadDialog() {
      if (this.uploadingScannedPdf) {
        return;
      }
      if (this.$refs.scannedPdfInput) {
        this.$refs.scannedPdfInput.value = "";
        this.$refs.scannedPdfInput.click();
      }
    },

    async onScannedPdfSelected(event) {
      const file =
        event && event.target && event.target.files && event.target.files[0]
          ? event.target.files[0]
          : null;

      if (!file) {
        return;
      }

      const hasPdfMime = file.type === "application/pdf";
      const hasPdfExtension = /\.pdf$/i.test(file.name || "");

      if (!hasPdfMime && !hasPdfExtension) {
        this.showSnackbar("Please select a single PDF file.");
        return;
      }

      await this.uploadScannedPdf(file);
    },

    async uploadScannedPdf(file) {
      this.uploadingScannedPdf = true;
      this.loadingSpinner = true;

      const payload = new FormData();
      payload.append("uploaded_file", file, file.name);

      try {
        const seasonId = Cookie.get("season_id");
        if (!seasonId) {
          this.showSnackbar("Active season is not selected.");
          return;
        }

        const response = await this.$axios.post(
          `/sponsorships/${seasonId}/print-ticket/match`,
          payload
        );
        this.applyUploadResponse(response && response.data ? response.data : {});
      } catch (err) {
        const message =
          err && err.response && err.response.data && err.response.data.error
            ? err.response.data.error
            : "We couldn't upload this PDF.";
        this.showSnackbar(message);
      } finally {
        this.uploadingScannedPdf = false;
        this.loadingSpinner = false;
      }
    },

    applyUploadResponse(payload) {
      const totalOrders = Number(payload.total_orders || 0);
      const verifiedOrders = Number(payload.verified_orders || 0);
      const responseOrders = Array.isArray(payload.orders) ? payload.orders : [];
      const verifiedIds = responseOrders
        .filter((order) => order && order.verified && order.id != null && order.id !== "")
        .map((order) => String(order.id));

      this.filterOrdersByVerifiedIds(verifiedIds);

      if (totalOrders > 0 && totalOrders === verifiedOrders) {
        this.showSnackbar(`All ${verifiedOrders} orders were verified.`);
        return;
      }

      const issues = responseOrders
        .filter((order) => !order || !order.verified)
        .map((order) => {
          const page = order && order.page ? order.page : "?";

          if (!order || order.id == null || order.id === "") {
            return `page ${page}: can't read sponsorship id`;
          }

          return `page ${page}: can't verify sponsorship ${order.id}`;
        });

      const summary = `Verified ${verifiedOrders}/${totalOrders}.`;
      if (!issues.length) {
        this.showSnackbar(summary);
        return;
      }

      this.showSnackbar(`${summary} Missing parts: ${issues.join("; ")}`);
    },

    filterOrdersByVerifiedIds(verifiedIds) {
      if (!Array.isArray(verifiedIds) || verifiedIds.length === 0) {
        this.orders = [];
        this.filteredOrders = [];
        return;
      }

      const matchedOrders = this.orders.filter((order) =>
        order.mediaType === "sponsorship" && verifiedIds.includes(String(order.id))
      );

      this.orders = matchedOrders;
      this.filteredOrders = matchedOrders;
    },

    handleScroll() {
      const scrollable =
        document.documentElement.scrollHeight > window.innerHeight;
      this.showScrollToTop = scrollable && window.scrollY > 50;
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    inputSearch(searchStr) {
      let _this = this;

      setTimeout(function() {
        _this.filteredOrders = _this.orders.filter((x) =>
          _this.filterFunction(x, searchStr)
        );
        _this.$forceUpdate();
      }, 1200);
    },

    async loadInitialOrders() {
      this.loadingSpinner = true;
      try {
        const sponsorshipsRaw = (
          await this.$axios.get(
            `/sponsorships?season_id=${Cookie.get("season_id")}&media_id=6`
          )
        ).data;
        const signageRaw = (
          await this.$axios.get(
            `/signage-orders?season_id=${Cookie.get("season_id")}`
          )
        ).data;

        const sponsorships = Array.isArray(sponsorshipsRaw)
          ? sponsorshipsRaw.filter(Boolean)
          : [];
        const signage = Array.isArray(signageRaw) ? signageRaw.filter(Boolean) : [];

        const combined = [...sponsorships, ...signage].map((order) =>
          this.initializeOrder(order)
        );

        this.orders = combined;
        this.filteredOrders = combined.filter((x) => {
          const sponsorshipStatus =
            typeof x.sponsorship_status !== "undefined"
              ? x.sponsorship_status
              : typeof x.sponsorship_status_id !== "undefined"
                ? x.sponsorship_status_id
                : x.status;
          const isBlockedSponsorship =
            x.mediaType === "sponsorship" && Number(sponsorshipStatus) === 49;
          return x.status !== 40 && !isBlockedSponsorship;
        });
      } catch (err) {
        this.snackbar.message = err.message;
        this.snackbar.show = true;
      } finally {
        this.loadingSpinner = false;
      }
    },

    setResultsOrders(orders) {
      let tempOrders = [];
      orders.forEach((el) => tempOrders.push(this.initializeOrder(el)));

      console.log(tempOrders);

      this.orders = tempOrders;
      this.filteredOrders = tempOrders;
      this.filteredOrders = this.filteredOrders.filter((x) => {
        const sponsorshipStatus =
          typeof x.sponsorship_status !== "undefined"
            ? x.sponsorship_status
            : typeof x.sponsorship_status_id !== "undefined"
              ? x.sponsorship_status_id
              : x.status;
        const isBlockedSponsorship =
          x.mediaType === "sponsorship" && Number(sponsorshipStatus) === 49;
        return x.status !== 40 && !isBlockedSponsorship;
      });
    },

    loadingSearchResults(isLoading) {
      this.loadingSpinner = isLoading;
    },
  },
};
</script>

<style lang="scss" scoped>
@include scroller-styles;

.md-tabs {
  @include respond(xsmall) {
    width: calc(100% + 48px);
    transform: translateX(-24px);
  }
}

/deep/ .md-tabs-navigation .md-button {
  @include respond(small) {
    min-width: 48px;
  }
}

.filter-orders__field {
  width: 100%;
  max-width: 320px;
}

.vendor-dashboard__filters {
  display: grid;

  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  grid-gap: 1.6rem 3.2rem;
  margin-bottom: 2rem;
  padding: 1rem 2.4rem 2.4rem;
  border: 2px solid $color-primary;
  border-radius: 5px;
  box-shadow: 0 4px 7px rgba(0, 0, 0, 0.42);

  & > * {
    margin: 0;
  }
}

.component-wrap {
  width: 95%;
}

@include scroller-styles;

.boxes {
  width: 100%;
}

.filter-orders__field {
  width: 100%;
  max-width: 320px;
}

.init-search-desktop {
  padding-top: 10px;

  .no-result-message {
    p {
      padding-top: 120px;
      text-align: center;
      font-weight: 700;
      line-height: 57px;
      font-style: normal;
      font-size: 48px;
    }
  }
}

.component-wrap {
  @include respond(small) {
    padding: 0px !important;
    margin: 0px !important;
    width: 100% !important;
  }
}

.main-hdr {
  margin-top: 10px;
}

.dashboard-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
}

.scroll-to-top-btn {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background-color: #cd0a2f;
  color: white;
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  opacity: 0.8;
  transition: opacity 0.3s ease;
  z-index: 9999;
}
</style>
